// ---------------------------------------------------------------------------
// API error parsing / friendly messages
//
// SELISE + backend proxy error bodies come in a few nested shapes, e.g.:
//
//   {
//     "success": false,
//     "message": "Organization signup failed: [{\"PropertyName\":\"Default\",
//                  \"ErrorMessage\":\"Setup already done.\",...}]",
//     "errorMessages": [{ "message": "...", "path": "" }]
//   }
//
// The `message` field frequently embeds a *stringified* JSON array of
// validation entries, so the useful text ("Setup already done.") is buried two
// layers deep. These helpers flatten that into a single human-readable message
// and provide friendly copy for network / 5xx failures so the UI never shows a
// raw JSON or HTML error blob.
// ---------------------------------------------------------------------------

export interface SeliseValidationEntry {
  PropertyName?: string;
  ErrorMessage?: string;
  ErrorCode?: string | null;
}

export interface ParsedSeliseError {
  /** Top-level message string, if any. */
  message: string | null;
  /** Validation entries extracted from an embedded `[{...}]` array. */
  validations: SeliseValidationEntry[];
}

// Pull an embedded `[{...}]` JSON array out of a message string.
const extractValidations = (message?: string | null): SeliseValidationEntry[] => {
  if (typeof message !== 'string') return [];
  const start = message.indexOf('[');
  const end = message.lastIndexOf(']');
  if (start === -1 || end <= start) return [];
  try {
    const arr = JSON.parse(message.slice(start, end + 1));
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

/**
 * Parse a SELISE / backend error body (object or raw JSON string) into its
 * top-level message and any embedded validation entries.
 */
export const parseSeliseError = (body: unknown): ParsedSeliseError => {
  let obj: unknown = body;
  if (typeof body === 'string') {
    try {
      obj = JSON.parse(body);
    } catch {
      // Non-JSON body (e.g. an HTML 502 page) — return it verbatim.
      return { message: body.trim() || null, validations: [] };
    }
  }

  if (!obj || typeof obj !== 'object') return { message: null, validations: [] };

  const o = obj as Record<string, any>;
  const rawMessage: unknown =
    o.message ?? o.errorMessages?.[0]?.message ?? o.error_description ?? o.error;
  const message = typeof rawMessage === 'string' ? rawMessage : null;

  return { message, validations: extractValidations(message) };
};

/** True when the given error body reports the ARC "Setup already done." case. */
export const isSetupAlreadyDone = (body: unknown): boolean => {
  const { message, validations } = parseSeliseError(body);
  if (validations.some((v) => /setup already done/i.test(v.ErrorMessage ?? ''))) return true;
  return /setup already done/i.test(message ?? '');
};

/** A fetch that never reached the server rejects with a TypeError. */
export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError && /fetch|network|load failed/i.test(error.message);

/** Friendly copy for HTTP status codes whose bodies carry no useful message. */
export const friendlyStatusMessage = (status: number): string => {
  if (status === 502 || status === 503 || status === 504) {
    return 'The service is temporarily unavailable. Please try again in a moment.';
  }
  if (status >= 500) return 'Something went wrong on our end. Please try again.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status === 401 || status === 403) {
    return 'Your session has expired. Please sign in again.';
  }
  return `Request failed (${status}). Please try again.`;
};

/**
 * Turn a raw error body + status into a clean, user-facing message. Prefers the
 * deepest meaningful text from the body, and falls back to status-based copy for
 * empty / non-JSON (e.g. HTML 502) responses.
 */
export const bodyErrorMessage = (text: string, status: number): string => {
  const { message, validations } = parseSeliseError(text);
  if (validations.length) {
    const joined = validations
      .map((v) => v.ErrorMessage?.trim())
      .filter(Boolean)
      .join(' ');
    if (joined) return joined;
  }
  if (message && !/<[a-z!]/i.test(message)) return message;
  return friendlyStatusMessage(status);
};

/**
 * Turn a non-OK fetch Response into a clean, user-facing error message.
 */
export const responseErrorMessage = async (response: Response): Promise<string> => {
  let text = '';
  try {
    text = await response.text();
  } catch {
    return friendlyStatusMessage(response.status);
  }
  return bodyErrorMessage(text, response.status);
};

/**
 * Resolve any caught error into a message safe to show the user. Converts
 * network failures to a connection hint and otherwise passes through the
 * (already-cleaned) Error message.
 */
export const getDisplayErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string => {
  if (isNetworkError(error)) {
    return 'Network error — please check your internet connection and try again.';
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
};
